/**
 * Getting Started Checklist
 *
 * Shows onboarding progress for new users.
 * Displayed when: account_age < 30 days OR domains < 3 OR !completed
 * Dismissible with persistence.
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  Globe,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Tag,
  ClipboardCheck,
  Users,
  X,
  ChevronDown,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";

interface OnboardingStep {
  id: string;
  label: string;
  description: string;
  completed: boolean;
  requiredFor: "all" | "paid";
  icon: React.ComponentType<{ className?: string }>;
  path: string;
}

interface OnboardingProgress {
  steps: {
    addDomain: boolean;
    verifyDns: boolean;
    reviewSources: boolean;
    advanceToQuarantine?: boolean;
    advanceToReject?: boolean;
    completeTask: boolean;
    inviteTeam: boolean;
  };
  checklistDismissed: boolean;
  completedCount: number;
  totalCount: number;
  showChecklist: boolean;
}

interface GettingStartedChecklistProps {
  isPaidTier: boolean;
  onDismiss?: () => void;
}

export function GettingStartedChecklist({
  isPaidTier,
  onDismiss,
}: GettingStartedChecklistProps) {
  const [progress, setProgress] = useState<OnboardingProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    api
      .getOnboardingProgress()
      .then((response) => {
        setProgress(response.progress);
        // Auto-collapse if more than 50% complete
        if (response.progress.completedCount > response.progress.totalCount / 2) {
          setExpanded(false);
        }
      })
      .catch((err) => {
        console.error("Failed to load onboarding progress:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await api.dismissOnboardingChecklist();
      onDismiss?.();
    } catch (err) {
      console.error("Failed to dismiss checklist:", err);
    } finally {
      setDismissing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 animate-pulse">
        <div className="h-6 bg-amber-200/50 rounded w-48 mb-2" />
        <div className="h-4 bg-amber-200/30 rounded w-64" />
      </div>
    );
  }

  if (!progress || !progress.showChecklist || progress.checklistDismissed) {
    return null;
  }

  const steps: OnboardingStep[] = [
    {
      id: "add_domain",
      label: "Add your first domain",
      description: "Start monitoring your email authentication",
      completed: progress.steps.addDomain,
      requiredFor: "all",
      icon: Globe,
      path: "/domains",
    },
    {
      id: "verify_dns",
      label: "Verify DNS records",
      description: "Confirm your DMARC record is published",
      completed: progress.steps.verifyDns,
      requiredFor: "all",
      icon: Shield,
      path: "/domains",
    },
    {
      id: "review_sources",
      label: "Review sending sources",
      description: "Identify who sends email on your behalf",
      completed: progress.steps.reviewSources,
      requiredFor: "all",
      icon: Tag,
      path: "/sources",
    },
    {
      id: "advance_to_quarantine",
      label: "Advance to p=quarantine",
      description: "Start enforcing DMARC for spoofing protection",
      completed: progress.steps.advanceToQuarantine ?? false,
      requiredFor: "all",
      icon: ShieldAlert,
      path: "/domains",
    },
    {
      id: "advance_to_reject",
      label: "Advance to p=reject",
      description: "Achieve full DMARC enforcement",
      completed: progress.steps.advanceToReject ?? false,
      requiredFor: "all",
      icon: ShieldCheck,
      path: "/domains",
    },
    ...(isPaidTier
      ? [
          {
            id: "complete_task",
            label: "Complete a remediation task",
            description: "Take action to improve authentication",
            completed: progress.steps.completeTask,
            requiredFor: "paid" as const,
            icon: ClipboardCheck,
            path: "/alerts?tab=tasks",
          },
          {
            id: "invite_team",
            label: "Invite a team member",
            description: "Collaborate with your team",
            completed: progress.steps.inviteTeam,
            requiredFor: "paid" as const,
            icon: Users,
            path: "/settings?tab=team",
          },
        ]
      : []),
  ];

  const visibleSteps = steps.filter(
    (s) => s.requiredFor === "all" || (s.requiredFor === "paid" && isPaidTier)
  );
  const completedSteps = visibleSteps.filter((s) => s.completed).length;
  const progressPercentage = Math.round((completedSteps / visibleSteps.length) * 100);

  // Find first incomplete step
  const nextStep = visibleSteps.find((s) => !s.completed);

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-3 flex-1 text-left"
        >
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-800">Getting Started</h3>
              <span className="text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                {completedSteps}/{visibleSteps.length} complete
              </span>
            </div>
            {/* Progress bar */}
            <div className="mt-2 h-1.5 bg-amber-200 rounded-full overflow-hidden max-w-xs">
              <div
                className="h-full bg-amber-500 rounded-full transition-all duration-500"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
          {expanded ? (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-slate-400" />
          )}
        </button>
        <button
          onClick={handleDismiss}
          disabled={dismissing}
          className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-amber-100 rounded-lg transition-colors ml-2"
          title="Dismiss checklist"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 pb-4 pt-0">
          <div className="space-y-2">
            {visibleSteps.map((step, index) => (
              <Link
                key={step.id}
                to={step.path}
                className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                  step.completed
                    ? "bg-emerald-50 border border-emerald-100"
                    : nextStep?.id === step.id
                    ? "bg-white border-2 border-amber-300 shadow-sm"
                    : "bg-white/50 border border-transparent hover:bg-white hover:border-amber-200"
                }`}
              >
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    step.completed
                      ? "bg-emerald-500 text-white"
                      : "bg-slate-200 text-slate-500"
                  }`}
                >
                  {step.completed ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <span className="text-xs font-medium">{index + 1}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className={`text-sm font-medium ${
                      step.completed ? "text-emerald-700" : "text-slate-700"
                    }`}
                  >
                    {step.label}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {step.description}
                  </div>
                </div>
                <step.icon
                  className={`w-4 h-4 flex-shrink-0 ${
                    step.completed ? "text-emerald-500" : "text-slate-400"
                  }`}
                />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
